<template>
  <v-dialog v-model="internalValue" max-width="900" scrollable>
    <v-card class="ds-dialog">
      <v-card-title class="ds-dialog-header d-flex align-center">
        <v-icon color="white" class="mr-2 header-icon">mdi-file-eye-outline</v-icon>
        <span class="ds-dialog-title">{{ headerTitle }}</span>
        <v-spacer />
        <v-chip
          v-if="hasLetterhead"
          x-small
          dark
          class="lh-chip mr-2"
        >
          <v-icon size="11" left>mdi-file-image-check</v-icon>
          Com timbre
        </v-chip>
        <v-chip
          v-else
          x-small
          dark
          class="lh-chip lh-chip--default mr-2"
        >
          <v-icon size="11" left>mdi-file-document-outline</v-icon>
          Formatação padrão
        </v-chip>
        <v-btn icon color="white" class="close-btn" @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="ds-dialog-body">
        <section class="ds-form-section">
          <div class="ds-form-section__head">
            <div class="ds-form-section__title">
              <v-icon small color="primary" class="mr-2">mdi-file-document-outline</v-icon>
              <span>Pré-visualização</span>
            </div>
          </div>
          <div v-if="loading" class="text-center py-8">
            <v-progress-circular indeterminate color="primary" />
            <div class="text-caption grey--text mt-3">Carregando timbre…</div>
          </div>

          <div v-else class="a4-frame">
            <div
              class="a4-page"
              :class="{ 'a4-page--with-letterhead': hasLetterhead }"
              :style="pageStyle"
            >
              <div class="a4-content" :class="contentClass" :style="contentStyle">
                <!-- Cabeçalho padrão (sem letterhead) -->
                <div v-if="!hasLetterhead" class="default-header">
                  <h1 :style="{ color: accentColor, borderBottomColor: accentColor }">
                    {{ documentTitle }}
                  </h1>
                </div>

                <!-- Corpo da prescrição -->
                <div class="prescription-html" v-html="safeContent" />

                <!-- Rodapé -->
                <div class="default-footer">
                  <p>
                    Documento gerado em {{ formattedDate }} às {{ formattedTime }}
                  </p>
                  <p v-if="selectedItemsLength != null">
                    Total de {{ totalLabel }}: {{ selectedItemsLength }}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>
      </v-card-text>

      <v-card-actions class="ds-dialog-actions">
        <v-btn outlined small color="primary" @click="close">
          <v-icon left>mdi-close</v-icon>
          Fechar
        </v-btn>
        <v-spacer />
        <v-btn
          color="primary"
          class="ds-save-btn"
          small
          depressed
          @click="onPrint"
          :disabled="loading"
        >
          <v-icon left small>mdi-printer</v-icon>
          Imprimir
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { printPrescription } from "@/features/patients/prescriptions/utils/printPrescription.js";
import { sanitizePrescriptionHtml } from "@/features/patients/prescriptions/utils/prescriptionSanitize";
import { fetchLetterheadImageUrl } from "@/services/letterheadService";

export default {
  name: "PrescriptionReviewDialog",
  props: {
    value: { type: Boolean, default: false },
    content: { type: String, default: "" },
    prescriptionType: { type: String, default: "medication" },
    selectedItemsLength: { type: Number, default: null },
    letterheadId: { type: [Number, String, null], default: null },
    contentArea: { type: Object, default: null },
  },
  emits: ["input"],

  data() {
    return {
      letterheadImageUrl: null,
      loading: false,
      _blobUrl: null,
    };
  },

  computed: {
    internalValue: {
      get() { return this.value; },
      set(v) { this.$emit("input", v); },
    },
    hasLetterhead() {
      return !!this.letterheadImageUrl;
    },
    /**
     * O corpo vem do contenteditable do editor, então é HTML de verdade e
     * precisa continuar formatado — escapar aqui apagaria os blocos coloridos.
     * Passa pelo mesmo whitelist que o PDF usa: mantém as tags de texto e as
     * classes de bloco, zera todo o resto (inclusive o botão "×" do editor,
     * que não deveria aparecer na revisão).
     */
    safeContent() {
      return sanitizePrescriptionHtml(this.content);
    },
    documentTitle() {
      switch (this.prescriptionType) {
        case "exam":       return "Solicitação de Exames";
        case "supplement": return "Prescrição de Suplementos";
        case "medication":
        default:           return "Prescrição de Medicamentos";
      }
    },
    headerTitle() {
      return `Revisar — ${this.documentTitle}`;
    },
    totalLabel() {
      switch (this.prescriptionType) {
        case "exam":       return "exames solicitados";
        case "supplement": return "suplementos prescritos";
        default:           return "medicamentos prescritos";
      }
    },
    accentColor() {
      switch (this.prescriptionType) {
        case "exam":       return "var(--ds-cat-green)";
        case "supplement": return "var(--ds-warning)";
        default:           return "var(--ds-primary)";
      }
    },
    contentClass() {
      return `prescription-type-${this.prescriptionType}`;
    },
    pageStyle() {
      if (!this.letterheadImageUrl) return {};
      return {
        backgroundImage: `url('${this.letterheadImageUrl}')`,
      };
    },
    // Quando há timbre + área salva, o conteúdo é posicionado exatamente
    // dentro do retângulo definido (em % da folha). Senão usa o padding padrão.
    contentStyle() {
      const a = this.contentArea;
      if (!this.hasLetterhead || !a) return {};
      const ok = ["top", "left", "width", "height"].every(
        (k) => Number.isFinite(+a[k]),
      );
      if (!ok) return {};
      return {
        top: `${a.top}%`,
        left: `${a.left}%`,
        width: `${a.width}%`,
        height: `${a.height}%`,
        right: "auto",
        bottom: "auto",
        padding: "0",
      };
    },
    formattedDate() {
      return new Date().toLocaleDateString("pt-BR");
    },
    formattedTime() {
      return new Date().toLocaleTimeString("pt-BR");
    },
  },

  watch: {
    value: {
      immediate: true,
      handler(open) {
        if (open) this.loadLetterhead();
        else this.cleanup();
      },
    },
    letterheadId() {
      if (this.value) this.loadLetterhead();
    },
  },

  beforeDestroy() {
    this.cleanup();
  },

  methods: {
    async loadLetterhead() {
      this.cleanup();
      if (!this.letterheadId) return;

      this.loading = true;
      try {
        const url = await fetchLetterheadImageUrl(this.letterheadId);
        this._blobUrl = url;
        this.letterheadImageUrl = url;
      } catch (err) {
        console.error("Erro ao carregar timbre:", err);
        // sem letterhead = cai pra formatação padrão
      } finally {
        this.loading = false;
      }
    },

    cleanup() {
      if (this._blobUrl) {
        URL.revokeObjectURL(this._blobUrl);
        this._blobUrl = null;
      }
      this.letterheadImageUrl = null;
    },

    close() {
      this.internalValue = false;
    },

    async onPrint() {
      // Para impressão precisamos passar uma URL de imagem que sobreviva
      // ao window.open. Como nossa URL é blob, vamos converter pra dataURL
      // — assim a nova janela consegue carregar.
      let dataUrl = null;
      if (this.letterheadImageUrl) {
        try {
          dataUrl = await this.blobUrlToDataUrl(this.letterheadImageUrl);
        } catch (e) {
          console.warn("Não foi possível converter timbre para dataURL:", e);
        }
      }

      try {
        printPrescription({
          content: this.content,
          prescriptionType: this.prescriptionType,
          selectedItemsLength: this.selectedItemsLength ?? 0,
          letterheadImageUrl: dataUrl,
          contentArea: this.contentArea,
        });
      } catch (err) {
        console.error("Erro ao imprimir:", err);
      }
    },

    async blobUrlToDataUrl(blobUrl) {
      const res = await fetch(blobUrl);
      const blob = await res.blob();
      return await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    },
  },
};
</script>

<style scoped>
.ds-dialog {
  border-radius: 4px !important;
  overflow: hidden;
  box-shadow: 0 24px 60px rgba(31, 77, 63, 0.18),
    0 4px 12px rgba(0, 0, 0, 0.06) !important;
}
.header-icon {
  position: relative;
  z-index: 1;
}
.lh-chip {
  background: rgba(31, 77, 63, 0.95) !important;
  color: var(--ds-white) !important;
  height: 22px !important;
  font-size: 10.5px !important;
  font-weight: 700 !important;
  letter-spacing: 0.3px;
  position: relative;
  z-index: 1;
}
.lh-chip--default {
  background: rgba(255, 255, 255, 0.22) !important;
}
.close-btn {
  position: relative;
  z-index: 1;
}
.close-btn:hover {
  background-color: rgba(255, 255, 255, 0.15) !important;
}

/* ═══ A4 simulada ═══ */
.a4-frame {
  display: flex;
  justify-content: center;
}

/* Proporção A4 — 210 × 297 mm.
   Usamos largura fixa em pixels e altura proporcional. */
.a4-page {
  width: 100%;
  max-width: 794px;          /* ≈ 210mm a 96dpi */
  aspect-ratio: 210 / 297;   /* mantém proporção A4 */
  background: var(--ds-surface-white);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  background-size: 100% 100%;
  background-position: top left;
  background-repeat: no-repeat;
  position: relative;
  overflow: hidden;
}

.a4-content {
  position: absolute;
  inset: 0;
  /* Quando NÃO há letterhead, o conteúdo começa no topo;
     quando há, deixamos um respiro de 110px no topo e 60px no rodapé
     pra não cobrir o cabeçalho/rodapé do timbre. */
  padding: 40px 56px;
  overflow: auto;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  font-size: 13px;
  line-height: 1.6;
  color: var(--ds-text);
}

.a4-page--with-letterhead .a4-content {
  /* Mais espaço no topo e no rodapé pra não invadir o cabeçalho/rodapé
     do letterhead — conteúdo fica centralizado no "miolo" da folha. */
  padding: 210px 64px 140px;
}

.default-header h1 {
  font-size: 22px;
  padding-bottom: 10px;
  margin: 0 0 24px;
  border-bottom: 3px solid;
  font-weight: 700;
  letter-spacing: -0.2px;
}

.default-footer {
  margin-top: 40px;
  padding-top: 12px;
  border-top: 1px solid var(--ds-blgrey-100);
  text-align: center;
  font-size: 11px;
  color: var(--ds-text-500);
}

/* HTML da prescrição — replica o estilo do editor + do printPrescription */
.prescription-html :deep(.medication-line) {
  display: block;
  background-color: var(--ds-blue-100);
  padding: 8px 12px;
  border-radius: 4px;
  margin: 6px 0;
  border-left: 4px solid var(--ds-primary);
  font-weight: 500;
}
.prescription-html :deep(.exam-line) {
  display: block;
  background-color: var(--ds-green-surface);
  padding: 8px 12px;
  border-radius: 4px;
  margin: 6px 0;
  border-left: 4px solid var(--ds-green);
  font-weight: 500;
}
.prescription-html :deep(.supplement-line) {
  display: block;
  background-color: var(--ds-primary-surface);
  padding: 8px 12px;
  border-radius: 4px;
  margin: 6px 0;
  border-left: 4px solid #fb8c00;
  font-weight: 500;
}
.prescription-html :deep(.medication-line button),
.prescription-html :deep(.exam-line button),
.prescription-html :deep(.supplement-line button) {
  /* botão de remover do editor — escondido no preview */
  display: none !important;
}
.prescription-html :deep(strong) { font-weight: 700; }
.prescription-html :deep(em) { font-style: italic; }
.prescription-html :deep(u) { text-decoration: underline; }
.prescription-html :deep(ul),
.prescription-html :deep(ol) { padding-left: 24px; margin: 8px 0; }

/* ═══ Action footer ═══ */
.ds-save-btn {
  font-size: 13px !important;
  font-weight: 600 !important;
  letter-spacing: 0.2px !important;
  text-transform: none !important;
  height: 38px !important;
  border-radius: 6px !important;
}
.ds-save-btn {
  box-shadow: 0 2px 8px rgba(31, 77, 63, 0.22) !important;
}

@media (max-width: 600px) {
  .a4-content { padding: 28px 24px; }
  .a4-page--with-letterhead .a4-content { padding: 150px 28px 100px; }
}
</style>
